import { useMemo, useState } from 'react';

/**
 * Wraps `fn` so it only calls through the first time; subsequent calls become no-ops that resolve
 * to `false` instead of invoking `fn` — even if the call args differ. This is the dedup mechanism
 * behind `useTrackOnce`, generalized so other once-per-mount side effects (e.g. a console log) can
 * share the exact same "once" semantics without going through real tracking.
 *
 * Pass `deps` to reset the "called" flag when they change, so the wrapped call fires again — see
 * `useTrackOnce` for why (e.g. a reused component instance across logical contexts).
 */
export function useCallOnce<FN extends (...args: never[]) => Promise<boolean>>(
	fn: FN,
	deps: readonly unknown[] = [],
): FN {
	const [called, setCalled] = useState<readonly unknown[] | false>(false);

	return useMemo(() => {
		// in case the returned function is called twice before the next render, we still want to make sure we only call through once
		let calledInSameRender = false;

		return (async (...args: Parameters<FN>) => {
			if ((called && areDepsEqual(called, deps)) || calledInSameRender) return false;
			setCalled(deps);
			calledInSameRender = true;
			return fn(...args);
		}) as FN;
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [fn, called, ...deps]); // eslint-disable-line react-hooks/use-memo
}

function areDepsEqual(deps1: readonly unknown[], deps2: readonly unknown[]) {
	// different sizes goes against rules of hooks, so it should not happen
	if (deps1.length !== deps2.length) return false;
	return deps1.every((val, idx) => val === deps2[idx]);
}
